<template>
  <div class="product-list-container">
    <!-- 商品管理头部 - 整合搜索筛选和操作按钮 -->
    <el-card class="product-header-card">
      <div class="header-content">
        <div class="header-left">
          <h2>商品管理</h2>
        </div>

        <!-- 搜索筛选 + 操作按钮 右侧区域 -->
        <div class="header-right">
          <!-- 搜索筛选控件 -->
          <div class="search-filter-group">
            <el-input
                v-model="searchQuery.keyword"
                placeholder="搜索商品名称"
                prefix-icon="el-icon-search"
                style="width: 200px; margin-right: 10px;"
                @keyup.enter.native="handleSearch"
                clearable
                @clear="handleSearch"
                size="small"
            />
            <el-select
                v-model="searchQuery.categoryId"
                placeholder="商品分类"
                style="width: 120px; margin-right: 10px;"
                clearable
                @change="handleSearch"
                size="small"
            >
              <el-option label="全部分类" value=""/>
              <el-option
                  v-for="category in categoryList"
                  :key="category.id"
                  :label="category.name"
                  :value="category.id"
              />
            </el-select>
            <el-select
                v-model="searchQuery.type"
                placeholder="商品类型"
                style="width: 100px; margin-right: 15px;"
                clearable
                @change="handleSearch"
                size="small"
            >
              <el-option label="全部类型" value=""/>
              <el-option label="服务商品" value="SERVICE"/>
              <el-option label="实物商品" value="PRODUCT"/>
            </el-select>
          </div>

          <!-- 操作按钮 -->
          <div class="action-buttons">
            <el-button type="primary" @click="handleAddProduct" size="small">
              <i class="el-icon-plus"></i>
              添加商品
            </el-button>
            <el-button @click="refreshData" size="small">
              <i class="el-icon-refresh"></i>
              刷新
            </el-button>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 商品表格 -->
    <el-card class="product-table-card">
      <div v-loading="loading">
        <el-table
            :data="productList"
            style="width: 100%"
            :header-cell-style="{background: '#f5f7fa', color: '#606266'}"
            @selection-change="handleSelectionChange"
        >
          <el-table-column type="selection" width="55"/>
          <el-table-column prop="id" label="商品ID" width="100"/>
          <el-table-column prop="name" label="商品名称" min-width="180">
            <template slot-scope="{row}">
              <div class="product-name-cell">
                <el-avatar
                    :size="40"
                    :src="row.image || ''"
                    class="product-avatar"
                >
                  {{ row.name.charAt(0) }}
                </el-avatar>
                <div class="product-info">
                  <div class="name">{{ row.name }}</div>
                  <div class="merchant" v-if="row.merchantName">{{ row.merchantName }}</div>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="categoryName" label="商品分类" width="120">
            <template slot-scope="{row}">
              {{ getCategoryName(row.categoryId) }}
            </template>
          </el-table-column>
          <el-table-column prop="price" label="售价(元)" width="120" align="right">
            <template slot-scope="{row}">
              {{ row.price ? (row.price / 100).toFixed(2) : '--' }}
            </template>
          </el-table-column>
          <el-table-column prop="originalPrice" label="原价(元)" width="120" align="right">
            <template slot-scope="{row}">
              {{ row.originalPrice ? (row.originalPrice / 100).toFixed(2) : '--' }}
            </template>
          </el-table-column>
          <el-table-column prop="description" label="商品描述" min-width="200">
            <template slot-scope="{row}">
              <el-tooltip :content="row.description" placement="top">
                <span class="description-text">{{ row.description || '--' }}</span>
              </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column prop="type" label="商品类型" width="100">
            <template slot-scope="{row}">
              <el-tag :type="row.type === 'PRODUCT' ? 'success' : 'warning'" size="small">
                {{ row.type === 'PRODUCT' ? '实物商品' : row.type === 'SERVICE' ? '服务商品' : '未知' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="salesCount" label="销量" width="100" align="right">
            <template slot-scope="{row}">
              {{ row.salesCount || 0 }}
            </template>
          </el-table-column>
          <el-table-column prop="stock" label="库存" width="100" align="right">
            <template slot-scope="{row}">
              {{ row.stock || 0 }}
            </template>
          </el-table-column>
          <el-table-column label="状态" width="70">
            <template slot-scope="{row}">
              <el-switch v-model="row.isOnSale" @change="(val) => handleToggleStatus(row, val)" />
            </template>
          </el-table-column>
          <el-table-column label="操作" width="200" fixed="right">
            <template slot-scope="{row}">
              <el-button type="text" size="small" @click="handleViewDetail(row)">
                <i class="el-icon-view"></i>
                详情
              </el-button>
              <el-button type="text" size="small" @click="handleEdit(row)">
                <i class="el-icon-edit"></i>
                编辑
              </el-button>
              <el-button
                  type="text"
                  size="small"
                  @click="handleDelete(row)"
                  style="color: #F56C6C"
              >
                <i class="el-icon-delete"></i>
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-container">
          <el-button
              type="danger"
              size="small"
              @click="handleBatchDelete"
              :disabled="selectedProducts.length === 0"
              style="margin-right: 10px;"
          >
            <i class="el-icon-delete"></i>
            批量删除
          </el-button>
          <el-pagination
              :current-page="pagination.currentPage"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="pagination.pageSize"
              :total="pagination.total"
              layout="total, sizes, prev, pager, next, jumper"
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </el-card>

    <!-- 商品详情弹窗 -->
    <el-dialog
        title="商品详情"
        :visible.sync="detailDialogVisible"
        width="700px"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        append-to-body
        destroy-on-close
        @close="handleDetailDialogClose"
    >
      <div v-loading="detailLoading" class="detail-dialog-content">
        <!-- 基本信息 -->
        <div class="detail-section">
          <div class="info-section">
            <div class="product-image-section">
              <el-image
                  v-if="currentProduct.image"
                  :src="currentProduct.image"
                  fit="cover"
                  class="product-main-image"
                  :preview-src-list="allDetailImages"
              >
                <div slot="error" class="image-error">
                  <i class="el-icon-picture-outline"></i>
                </div>
              </el-image>
              <div v-else class="image-placeholder">
                <i class="el-icon-picture-outline"></i>
                <span>暂无图片</span>
              </div>
            </div>

            <div class="product-base-info">
              <div class="info-row">
                <span class="label">商品ID</span>
                <span class="value">{{ currentProduct.id }}</span>
              </div>
              <div class="info-row">
                <span class="label">商品名称</span>
                <span class="value name">{{ currentProduct.name }}</span>
              </div>
              <div class="info-row">
                <span class="label">商品分类</span>
                <span class="value">{{ currentProduct.categoryName || '--' }}</span>
              </div>
              <div class="info-row">
                <span class="label">商品类型</span>
                <el-tag :type="getTypeColor(currentProduct.type)" size="small">
                  {{ getTypeText(currentProduct.type) }}
                </el-tag>
                <el-tag
                    v-if="currentProduct.tag"
                    type="danger"
                    size="small"
                    style="margin-left: 8px;"
                >
                  {{ currentProduct.tag }}
                </el-tag>
              </div>
              <div class="info-row">
                <span class="label">售价</span>
                <span class="value price">¥{{ formatPrice(currentProduct.price) }}</span>
              </div>
              <div class="info-row">
                <span class="label">原价</span>
                <span class="value original-price">¥{{ formatPrice(currentProduct.originalPrice) }}</span>
              </div>
              <div class="info-row">
                <span class="label">库存</span>
                <span class="value">{{ currentProduct.stock || 0 }}</span>
              </div>
              <div class="info-row">
                <span class="label">销量</span>
                <span class="value">{{ currentProduct.salesCount || 0 }}</span>
              </div>
              <div class="info-row">
                <span class="label">状态</span>
                <el-tag :type="currentProduct.status === 1 ? 'success' : 'info'" size="small">
                  {{ currentProduct.status === 1 ? '已上架' : '已下架' }}
                </el-tag>
              </div>
            </div>
          </div>
        </div>

        <!-- 商品描述 -->
        <div class="detail-section" v-if="currentProduct.description">
          <h4 class="section-title">商品描述</h4>
          <div class="description-content">{{ currentProduct.description }}</div>
        </div>

        <!-- 详情图片 -->
        <div class="detail-section" v-if="detailImageList.length > 0">
          <h4 class="section-title">详情图片</h4>
          <div class="detail-images-grid">
            <el-image
                v-for="(img, index) in detailImageList"
                :key="index"
                :src="img"
                fit="cover"
                class="detail-image-item"
                :preview-src-list="detailImageList"
            />
          </div>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="detailDialogVisible = false">关 闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import api from "@/utils/api"

export default {
  name: 'ProductList',
  data() {
    return {
      loading: false,
      storeId: null,

      searchQuery: {
        keyword: '',
        categoryId: '',
        type: ''
      },

      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },

      productList: [],
      categoryList: [],
      selectedProducts: [],

      detailDialogVisible: false,
      detailLoading: false,
      currentProduct: {},
      detailImageList: []
    }
  },

  computed: {
    allDetailImages() {
      const images = []
      if (this.currentProduct.image) images.push(this.currentProduct.image)
      return images.concat(this.detailImageList)
    }
  },

  mounted() {
    this.initData()
  },

  methods: {
    async initData() {
      await this.waitForStoreId()
      if (!this.storeId) { this.$message.warning('未获取到店铺信息，请联系管理员'); return }
      await Promise.all([this.loadCategoryList(), this.loadProductList()])
    },

    waitForStoreId() {
      this.getStoreIdFromVuex()
      if (this.storeId) return Promise.resolve()
      return new Promise((resolve) => {
        let attempts = 0
        const timer = setInterval(() => { this.getStoreIdFromVuex(); attempts++; if (this.storeId || attempts >= 30) { clearInterval(timer); resolve() } }, 100)
      })
    },

    getStoreIdFromVuex() { const adminInfo = this.$store.state.adminInfo; if (adminInfo && adminInfo.merchantId) this.storeId = adminInfo.merchantId },

    async loadCategoryList() {
      try { const res = await api.product_getCategoryList(); if (res.data.code === '000') this.categoryList = res.data.data.list || res.data.data || [] } catch (e) {}
    },

    async loadProductList() {
      this.loading = true
      try {
        const params = { merchantId: this.storeId, page: this.pagination.currentPage, pageSize: this.pagination.pageSize, keyword: this.searchQuery.keyword || undefined, categoryId: this.searchQuery.categoryId || undefined, type: this.searchQuery.type || undefined }
        const res = await api.product_getProductList(params, null)
        if (res.data.code === '000') {
          const data = res.data.data
          this.productList = (data.list || data.records || data || []).map(p => ({ ...p, isOnSale: p.status === 1 }))
          this.pagination.total = data.totalCount || data.total || this.productList.length
        }
      } catch (e) { this.$message.error('加载失败') }
      finally { this.loading = false }
    },

    getCategoryName(id) { const c = this.categoryList.find(c => c.id === id); return c ? c.name : '未知分类' },
    formatPrice(p) { if (!p) return '0.00'; return (p / 100).toFixed(2) },
    getTypeText(type) { const m = { 'PRODUCT': '实物商品', 'SERVICE': '服务商品' }; return m[type] || '未知类型' },
    getTypeColor(type) { const m = { 'PRODUCT': 'success', 'SERVICE': 'warning' }; return m[type] || 'info' },

    handleSearch() { this.pagination.currentPage = 1; this.loadProductList() },
    handleSizeChange(size) { this.pagination.pageSize = size; this.pagination.currentPage = 1; this.loadProductList() },
    handleCurrentChange(page) { this.pagination.currentPage = page; this.loadProductList() },
    refreshData() { this.searchQuery = { keyword: '', categoryId: '', type: '' }; this.pagination.currentPage = 1; this.loadProductList() },
    handleSelectionChange(val) { this.selectedProducts = val },
    handleAddProduct() { if (!this.storeId) this.getStoreIdFromVuex(); this.$router.push({ path: '/Index/Product/Add', query: { merchantId: this.storeId } }) },
    handleEdit(row) { this.$router.push({ path: '/Index/Product/Edit', query: { id: row.id, merchantId: row.merchantId || this.storeId } }) },

    async handleViewDetail(row) {
      this.detailDialogVisible = true
      this.detailLoading = true
      this.detailImageList = []
      try {
        const res = await api.product_getProductDetail({ id: row.id })
        if (res.data.code === '000') {
          this.currentProduct = res.data.data
          if (this.currentProduct.contentImageIds) {
            const ids = this.currentProduct.contentImageIds.split(',').filter(Boolean)
            const urls = await Promise.all(ids.map(id => this.getImageUrlById(id)))
            this.detailImageList = urls.filter(url => url)
          }
        }
      } catch (e) { this.$message.error('加载失败') }
      finally { this.detailLoading = false }
    },

    async getImageUrlById(id) {
      if (!id) return ''
      try { const res = await api.image_getImageById({ id }, null); const d = res.data || res; if (d.code === '000' && d.data) return d.data.filePath || d.data.url || '' } catch (e) {}
      return ''
    },

    handleDetailDialogClose() { this.currentProduct = {}; this.detailImageList = [] },

    async handleToggleStatus(row, val) {
      const s = val ? 1 : 2
      try {
        const res = await api.product_toggleProductStatus({ id: row.id, status: s }, null)
        if ((res.data || res).code === '000') { this.$set(row, 'status', s); this.$set(row, 'isOnSale', val); this.$message.success(s === 1 ? '已上架' : '已下架') }
        else { this.$set(row, 'isOnSale', !val) }
      } catch (e) { this.$set(row, 'isOnSale', !val) }
    },

    async handleDelete(row) {
      try { await this.$confirm('确定删除？', '提示', { type: 'warning' }); const res = await api.product_deleteProduct({ id: row.id }, { method: 'post' }); if ((res.data || res).code === '000') { this.$message.success('删除成功'); this.loadProductList() } } catch (e) {}
    },

    async handleBatchDelete() {
      try {
        await this.$confirm(`确定删除选中的${this.selectedProducts.length}个商品？`, '提示', { type: 'warning' })
        const res = await api.product_batchDeleteProduct({ ids: this.selectedProducts.map(i => i.id) }, { method: 'post' })
        if ((res.data || res).code === '000') { this.$message.success('删除成功'); this.loadProductList(); this.selectedProducts = [] }
      } catch (e) {}
    }
  }
}
</script>

<style scoped lang="scss">
.product-list-container { padding: 20px; }

.product-header-card {
  margin-bottom: 20px;
  .header-content { display: flex; justify-content: space-between; align-items: center; flex-wrap: nowrap; }
  .header-left { flex: 0 0 auto; h2 { margin: 0; font-size: 24px; color: #303133; } }
  .header-right { flex: 1 1 auto; display: flex; justify-content: flex-end; align-items: center; gap: 10px; .search-filter-group { display: flex; align-items: center; background: #f8f9fa; padding: 6px 12px; border-radius: 4px; border: 1px solid #e9ecef; } .action-buttons { display: flex; align-items: center; gap: 8px; } }
}

.product-table-card {
  .product-name-cell { display: flex; align-items: center; .product-avatar { margin-right: 12px; background-color: #409eff; color: white; font-weight: bold; } .product-info { .name { font-weight: 500; margin-bottom: 4px; } .merchant { font-size: 12px; color: #909399; } } }
  .description-text { display: inline-block; max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
}

.pagination-container { display: flex; justify-content: space-between; align-items: center; margin-top: 20px; padding-top: 20px; border-top: 1px solid #ebeef5; }

.detail-dialog-content {
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 8px;

  .detail-section { margin-bottom: 16px; .section-title { margin: 0 0 10px 0; padding-bottom: 8px; border-bottom: 1px solid #ebeef5; font-size: 14px; color: #303133; } }
  .info-section { display: flex; gap: 20px; .product-image-section { flex: 0 0 180px; .product-main-image { width: 180px; height: 180px; border-radius: 8px; border: 1px solid #ebeef5; } .image-placeholder { width: 180px; height: 180px; border-radius: 8px; border: 1px dashed #dcdfe6; display: flex; flex-direction: column; align-items: center; justify-content: center; color: #c0c4cc; font-size: 13px; i { font-size: 40px; margin-bottom: 8px; } } } .product-base-info { flex: 1; .info-row { display: flex; align-items: center; padding: 8px 0; border-bottom: 1px solid #f5f7fa; .label { width: 70px; color: #909399; font-size: 13px; flex-shrink: 0; } .value { color: #303133; font-size: 14px; &.name { font-size: 16px; font-weight: 600; } &.price { font-size: 18px; font-weight: bold; color: #f56c6c; } &.original-price { text-decoration: line-through; color: #c0c4cc; font-size: 13px; } } } } }
  .detail-images-grid { display: flex; flex-wrap: wrap; gap: 8px; .detail-image-item { width: 100px; height: 100px; border-radius: 4px; border: 1px solid #ebeef5; } }
  .description-content { padding: 10px; line-height: 1.6; color: #606266; font-size: 14px; background-color: #fafbfc; border-radius: 4px; white-space: pre-wrap; word-break: break-all; }
}

.image-error { display: flex; justify-content: center; align-items: center; height: 100%; background-color: #f5f7fa; color: #c0c4cc; font-size: 24px; }

@media (max-width: 1200px) { .product-header-card .header-content { flex-wrap: wrap; } .product-header-card .header-right { width: 100%; margin-top: 15px; justify-content: flex-start; } }
@media (max-width: 768px) { .product-list-container { padding: 12px; } .detail-dialog-content .info-section { flex-direction: column; .product-image-section { flex: none; display: flex; justify-content: center; } } }
</style>